import React, { useState, useEffect, useRef } from "react";
import PropTypes from "prop-types";
import {
  Dialog,
  DialogContent,
  DialogActions,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from "@material-ui/core";
import { useTranslation } from "react-i18next";
import { makeStyles } from "@material-ui/core/styles";

import { ExpandMore } from "@material-ui/icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faBullseye,
  faCogs,
  faChartLine,
  faLightbulb,
} from "@fortawesome/free-solid-svg-icons";
import PerfectScrollbar from "perfect-scrollbar";
import "assets/css/perfect-scrollbar-custom.css";
import Button from "../CustomButtons/Button";
import ModalHeader from "../ModalHeader";
import GridContainer from "../Grid/GridContainer";
import GridItem from "../Grid/GridItem";

import modalStyle from "assets/jss/material-dashboard-pro-react/modalStyle";
import { grayColor } from "assets/jss/material-dashboard-pro-react.js";

const styles = (theme) => ({
  ...modalStyle(theme),
  helpSection: {
    marginBottom: "16px",
  },
  helpSectionTitle: {
    display: "flex",
    alignItems: "center",
    color: theme.palette.primary.main,
    fontWeight: "600",
  },
  helpSectionIcon: {
    marginRight: "16px",
    fontSize: "20px",
    color: grayColor[0],
    width: "20px",
    textAlign: "center",
  },
  helpContent: {
    paddingTop: "16px",
  },
  helpItem: {
    marginBottom: "16px",
    padding: "6px 12px",
    backgroundColor: theme.palette.background.default,
    // borderRadius: "6px",
    borderLeft: `2px solid ${theme.palette.primary.main}`,
  },
  helpItemTitle: {
    fontWeight: "400",
    marginBottom: "8px",
    color: theme.palette.text.primary,
  },
  helpItemDescription: {
    color: theme.palette.text.secondary,
    lineHeight: "1.5",
  },
  scrollableContent: {
    maxHeight: "60vh",
    overflow: "hidden",
    position: "relative",
    "&.ps": {
      overflow: "hidden !important",
    },
  },
  accordion: {
    boxShadow: "none",
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: "8px",
    marginBottom: "5px !important",
    "&:before": {
      display: "none",
    },
    "&.Mui-expanded": {
      marginBottom: "5px !important",
    },
  },
  accordionSummary: {
    backgroundColor: theme.palette.background.paper,
    borderRadius: "8px",
    "&:hover": {
      backgroundColor: theme.palette.action.hover,
    },
  },
  accordionDetails: {
    padding: "0 16px",
    backgroundColor: theme.palette.background.default,
  },
});

let ps;

const HelpDialog = ({ open, setOpen, content, title: titleProp }) => {
  const useStyles = makeStyles(styles);
  const classes = useStyles();
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState(false);
  const scrollableContentRef = useRef();

  const handleClose = () => {
    setOpen(false);
  };

  const handleAccordionChange = (panel) => (event, isExpanded) => {
    setExpanded(isExpanded ? panel : false);
  };

  // Initialize PerfectScrollbar
  useEffect(() => {
    if (
      navigator.platform.indexOf("Win") > -1 &&
      scrollableContentRef.current
    ) {
      // Destroy existing instance if any
      if (ps) {
        ps.destroy();
      }

      ps = new PerfectScrollbar(scrollableContentRef.current, {
        suppressScrollX: true,
        suppressScrollY: false,
      });

      // Update PerfectScrollbar when content changes
      const updateScrollbar = () => {
        if (ps) {
          ps.update();
        }
      };

      // Update after a short delay to ensure content is rendered
      setTimeout(updateScrollbar, 100);
    }
    return function cleanup() {
      if (navigator.platform.indexOf("Win") > -1 && ps) {
        ps.destroy();
        ps = null;
      }
    };
  }, [open, expanded]); // Re-initialize when dialog opens/closes or accordion state changes

  // Icon mapping function
  const getIconComponent = (iconName) => {
    const iconProps = { className: classes.helpSectionIcon };

    switch (iconName) {
      case "Purpose":
        return <FontAwesomeIcon icon={faBullseye} {...iconProps} />;
      case "BasicFunctionality":
        return <FontAwesomeIcon icon={faCogs} {...iconProps} />;
      case "Analysis":
        return <FontAwesomeIcon icon={faChartLine} {...iconProps} />;
      case "OtherFeatures":
        return <FontAwesomeIcon icon={faLightbulb} {...iconProps} />;
      default:
        return <FontAwesomeIcon icon={faBullseye} {...iconProps} />;
    }
  };

  return (
    <Dialog
      classes={{
        root: classes.modalRoot,
        paper: classes.modal,
      }}
      open={open}
      keepMounted
      onClose={handleClose}
      aria-labelledby="help-modal-slide-title"
      aria-describedby="help-modal-slide-description"
      fullWidth
      maxWidth="md"
    >
      <ModalHeader
        id="help-modal-slide-title"
        title={titleProp || t("help.pl_explorer.title")}
        onClose={handleClose}
        closeLabel={t("common.close")}
      />

      <DialogContent
        id="help-modal-slide-description"
        className={classes.modalBody}
      >
        <div
          className={`${classes.scrollableContent} ps`}
          ref={scrollableContentRef}
        >
          <GridContainer>
            {content.map((topic, topicIndex) => (
              <GridItem xs={12} key={topicIndex}>
                <Accordion
                  className={classes.accordion}
                  expanded={expanded === `panel${topicIndex}`}
                  onChange={handleAccordionChange(`panel${topicIndex}`)}
                >
                  <AccordionSummary
                    expandIcon={<ExpandMore />}
                    className={classes.accordionSummary}
                  >
                    <div className={classes.helpSectionTitle}>
                      {getIconComponent(topic.icon)}
                      {topic.title}
                    </div>
                  </AccordionSummary>
                  <AccordionDetails className={classes.accordionDetails}>
                    <div className={classes.helpContent}>
                      {topic.items.map((item, itemIndex) => (
                        <div key={itemIndex} className={classes.helpItem}>
                          <div className={classes.helpItemTitle}>
                            {item.title}
                          </div>
                          <div className={classes.helpItemDescription}>
                            {item.description}
                          </div>
                        </div>
                      ))}
                    </div>
                  </AccordionDetails>
                </Accordion>
              </GridItem>
            ))}
          </GridContainer>
        </div>
      </DialogContent>

      <DialogActions className={classes.modalFooter}>
        <Button onClick={handleClose} color="client" simple>
          {t("ok")}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

HelpDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  setOpen: PropTypes.func.isRequired,
  title: PropTypes.string,
  content: PropTypes.arrayOf(
    PropTypes.shape({
      icon: PropTypes.string,
      title: PropTypes.string.isRequired,
      items: PropTypes.arrayOf(
        PropTypes.shape({
          title: PropTypes.string.isRequired,
          description: PropTypes.string.isRequired,
        }),
      ).isRequired,
    }),
  ).isRequired,
};

export default HelpDialog;
